import { useState, useEffect, useRef, FC } from 'react';
import ReactDOM from 'react-dom';
import classnames from 'classnames';

import { localStorageWrapper } from '@hh.ru/browser-api-utils';
import { Checkbox, HSpacing, Loader, useBreakpoint } from '@hh.ru/magritte-ui';
import BrokerClient from '@hh.ru/react-filter-components-plugin/lib/client/brokerClient';

import styles from './loader.less';

type DevBuildLoaderProps = { serviceName: string };

let _DevBuildLoader: FC<DevBuildLoaderProps> = () => null;

const NON_BREAKING_SPACE = '\u00a0';
const MAX_PERCENTAGE = 100;

const WRAPPER_ID = 'dev-loader-wrapper';
let reloadTimeoutId: NodeJS.Timeout | null = null;
const STATIC_UPLOAD_WAIT_MS = 1500;

interface BuildProgressUpdateAction {
    action: 'build-progress-update';
    details: {
        progress: number;
    };
}

interface BuildErrorAction {
    action: 'build-error';
    details: {
        message: string;
    };
}

interface BuildErrorClearAction {
    action: 'build-error-clear';
}

type BrokerMessage = BuildProgressUpdateAction | BuildErrorAction | BuildErrorClearAction;

if (process.env.HH_BUILD_MODE === 'development') {
    const __DevBuildLoader: FC<DevBuildLoaderProps> = ({ serviceName }) => {
        const [percent, setPercent] = useState(100);

        const [show, setShow] = useState(false);
        const [waitForReload, setWaitForReload] = useState(false);
        const [compilationError, setCompilationError] = useState<string | null>(null);
        const previousPercent = useRef(percent);
        const [liveReload, setLiveReload] = useState(false);
        const { isXS } = useBreakpoint();
        const localStorageKey = `dev_build_settings_${serviceName}`;

        useEffect(() => {
            if (!liveReload) {
                return;
            }

            if (previousPercent.current !== percent && percent === MAX_PERCENTAGE) {
                setWaitForReload(true);
                /* Статика заливается какое-то время в под с сервисом*/
                reloadTimeoutId = setTimeout(() => {
                    window.location.reload();
                }, STATIC_UPLOAD_WAIT_MS);
            } else if (reloadTimeoutId) {
                // этот блок нужен для случая, когда в процессе сборки поменялся еще раз файл, чтобы не перезагружать
                // страницу по несколько раз, а сбросить текущее состояние
                clearTimeout(reloadTimeoutId);
                setWaitForReload(false);
            }

            previousPercent.current = percent;
        }, [percent, liveReload]);

        useEffect(() => {
            if (!BrokerClient) {
                return undefined;
            }

            const onMessage = (message: MessageEvent) => {
                try {
                    const data = JSON.parse(message.data as string) as BrokerMessage;
                    switch (data?.action) {
                        case 'build-progress-update':
                            setPercent(data.details.progress);
                            break;
                        case 'build-error':
                            setCompilationError(data.details.message);
                            break;
                        case 'build-error-clear':
                            setCompilationError(null);
                            break;
                    }
                } catch (e) {
                    console.error('Decode error');
                }
            };

            BrokerClient.addEventListener('message', onMessage);

            return () => {
                BrokerClient?.removeEventListener('message', onMessage);
            };
        }, []);

        useEffect(() => {
            const options = localStorageWrapper.getItem(localStorageKey);

            if (options) {
                try {
                    const { liveReload } = JSON.parse(options) as { liveReload: boolean };
                    setLiveReload(liveReload);
                } catch (_ignore) {} // eslint-disable-line
            }
        }, [localStorageKey]);

        useEffect(() => {
            const devLoaderWrapper = document.querySelector(`#${WRAPPER_ID}`);

            if (devLoaderWrapper === null) {
                const wrapper = document.createElement('div');
                wrapper.classList.add(styles.containerForDevLoaders);
                wrapper.id = WRAPPER_ID;

                document.body.prepend(wrapper);
            }

            const globalVars = serviceName === 'xhh' ? window.globalVars : window.globalServiceVars[serviceName];
            if (!globalVars.devLoaderInited) {
                setShow(true);
                globalVars.devLoaderInited = true;
            }
        }, [serviceName]);

        const hide = (percent === MAX_PERCENTAGE && !waitForReload) || !show;

        if (hide) {
            return null;
        }

        const buildInProgress = percent > 0;

        const renderBuildStep = () => {
            if (waitForReload) {
                return (
                    <>
                        refreshing page
                        <HSpacing default={4} />
                        <Loader size={16} initial="positive" />
                    </>
                );
            }
            if (compilationError) {
                return 'compilation error';
            }
            if (buildInProgress) {
                return (
                    <progress className={styles.progress} max={MAX_PERCENTAGE} value={percent}>
                        {percent}
                    </progress>
                );
            }
            return null;
        };

        return (
            <>
                {show &&
                    ReactDOM.createPortal(
                        <>
                            <div
                                className={classnames(styles.wrapper, {
                                    [styles.wrapperError]: !!compilationError,
                                })}
                            >
                                <label className={styles.progressLabel}>
                                    {serviceName}:{NON_BREAKING_SPACE}
                                    {buildInProgress ? <>Build in progress:{NON_BREAKING_SPACE}</> : 'Build started'}
                                    {renderBuildStep()}
                                </label>
                                <label className={styles.checkboxLabel}>
                                    <Checkbox
                                        checked={liveReload}
                                        onChange={(event) => {
                                            setLiveReload(event.target.checked);
                                            localStorageWrapper.setItem(
                                                localStorageKey,
                                                JSON.stringify({ liveReload: event.target.checked })
                                            );
                                        }}
                                    />
                                    <HSpacing default={4} />
                                    <span>{isXS ? 'LR' : 'Live reload'}</span>
                                </label>
                            </div>
                            {!!compilationError && (
                                <div className={styles.errorWrapper}>
                                    <pre>{compilationError}</pre>
                                </div>
                            )}
                        </>,
                        document.querySelector(`#${WRAPPER_ID}`) as HTMLDivElement
                    )}
            </>
        );
    };

    _DevBuildLoader = __DevBuildLoader;
}

const DevBuildLoader = _DevBuildLoader;

export default DevBuildLoader;
